import {
  Popover,
  PopoverInteractionKind,
  PopoverPosition,
  Position,
  Tag,
} from "@blueprintjs/core";
import "styled-components/macro";
import React, { ReactNode } from "react";
import { DescriptionItemConfig, FlatDescriptions } from "./flat-descriptions";
import { buildDescriptionItem } from "./overview--description-item";
import { Intent } from "@blueprintjs/core/lib/esm/common/intent";
import {
  getFormatter,
  useRelatedsSum,
} from "../entities/helpers/relation-agg-cr";
import { TableRow } from "../store/table-model-factory";
import { floatVF } from "./AgTable/value-formatting";

export function HierarchicalRelatedEntitiesTag({
  itemConfig,
  children,
  data,
  position,
}: HierarchicalRelatedEntitiesTagProps): JSX.Element {
  const getTagIntent =
    itemConfig.extra?.getTagIntent ?? ((data) => Intent.PRIMARY);
  const tagIntent = getTagIntent(data);
  return (
    <Popover
      autoFocus={false}
      usePortal={true}
      boundary={"viewport"}
      position={position ?? Position.BOTTOM}
      content={
        <HierarchicalRelatedEntitiesTag_PopoverContent
          itemConfig={itemConfig}
          data={data}
        />
      }
      interactionKind={PopoverInteractionKind.HOVER}
    >
      <Tag
        interactive={true}
        intent={tagIntent}
        css={`
          font-size: 14px;
          padding: 3px 7px;
        `}
      >
        {children}
      </Tag>
    </Popover>
  );
}

export function HierarchicalRelatedEntitiesSumTag({
  itemConfig,
  data,
  ...restProps
}: Omit<HierarchicalRelatedEntitiesTagProps, "children">): JSX.Element {
  const value = data?.[itemConfig.field];
  const summation = useRelatedsSum(
    value.entityName,
    value.entityIds,
    itemConfig.extra.foreignField
  );
  const formatter = getFormatter(itemConfig.extra.type) ?? floatVF;
  const formattedSummation = formatter(summation);
  return (
    <HierarchicalRelatedEntitiesTag
      itemConfig={itemConfig}
      data={data}
      {...restProps}
    >
      {formattedSummation}
    </HierarchicalRelatedEntitiesTag>
  );
}

function HierarchicalRelatedEntitiesTag_PopoverContent({
  itemConfig,
  data,
}: HierarchicalRelatedEntitiesTagPopoverContentProps): JSX.Element {
  return (
    <div
      css={`
        padding: 0;
      `}
    >
      <FlatDescriptions withoutBoxShadow>
        {itemConfig.childItemConfigs.map((itemConfig, key) =>
          buildDescriptionItem({
            key,
            itemConfig,
            data,
          })
        )}
      </FlatDescriptions>
    </div>
  );
}

export interface HierarchicalRelatedEntitiesTagPopoverContentProps {
  itemConfig: DescriptionItemConfig;
  data: TableRow;
}

export interface HierarchicalRelatedEntitiesTagProps
  extends HierarchicalRelatedEntitiesTagPopoverContentProps {
  children: ReactNode | ReactNode[];
  position?: PopoverPosition;
}
